import { LogLevel } from '@azure/msal-browser';

/**
 * Runtime MSAL configuration fetched from backend.
 * This ensures the frontend always uses the same configuration as the backend.
 */
let runtimeConfig = null;

/**
 * Fetch authentication configuration from backend at runtime.
 * This allows the configuration to stay in sync with backend environment variables.
 */
export const fetchAuthConfig = async () => {
    if (runtimeConfig) {
        return runtimeConfig;
    }

    try {
        const response = await fetch('/api/config/auth');
        if (!response.ok) {
            throw new Error(`Failed to fetch auth config: ${response.status}`);
        }
        const config = await response.json();
        runtimeConfig = config;
        console.log('Loaded runtime auth configuration from backend');
        return config;
    } catch (error) {
        console.error('Failed to load auth configuration:', error);
        // Fallback to environment variables if backend is unavailable
        return {
            msal: {
                clientId: process.env.REACT_APP_MSAL_CLIENT_ID,
                authority: `https://login.microsoftonline.com/${process.env.REACT_APP_MSAL_TENANT_ID}`,
                redirectUri: process.env.REACT_APP_MSAL_REDIRECT_URI || window.location.origin,
                postLogoutRedirectUri: '/',
            },
            scopes: []
        };
    }
};

/**
 * Build MSAL configuration object from runtime config.
 * Call this after fetchAuthConfig() completes.
 */
export const buildMsalConfig = (authConfig) => {
    return {
        auth: {
            clientId: authConfig.msal.clientId,
            authority: authConfig.msal.authority,
            redirectUri: authConfig.msal.redirectUri,
            postLogoutRedirectUri: authConfig.msal.postLogoutRedirectUri,
            navigateToLoginRequestUrl: false,
        },
        cache: {
            cacheLocation: 'sessionStorage',
            storeAuthStateInCookie: false,
        },
        system: {
            loggerOptions: {
                loggerCallback: (level, message, containsPii) => {
                    if (containsPii) {
                        return;
                    }
                    switch (level) {
                        case LogLevel.Error:
                            console.error(message);
                            return;
                        case LogLevel.Warning:
                            console.warn(message);
                            return;
                        default:
                            return;
                    }
                },
            },
        },
    };
};

/**
 * Build login request with scopes from runtime config.
 */
export const buildLoginRequest = (authConfig) => {
    return {
        scopes: authConfig.scopes || [],
    };
};

// Legacy export for backward compatibility (will be populated after fetchAuthConfig)
export let msalConfig = null;
export let loginRequest = null;
